import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { useI18n } from "../i18n/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Search, Lock, Sparkles, GraduationCap } from "lucide-react";

const BANDS = ["all", "foundation", "builder", "innovator"];

export default function Courses() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [courses, setCourses] = useState(null);
  const [enrolledIds, setEnrolledIds] = useState(new Set());
  const [band, setBand] = useState("all");
  const [query, setQuery] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    api
      .listCourses()
      .then(setCourses)
      .catch((e) => setError(e.message));
  }, []);

  useEffect(() => {
    if (!user) return;
    api
      .myEnrollments()
      .then((rows) => setEnrolledIds(new Set(rows.map((r) => r.course.course_id))))
      .catch(() => {});
  }, [user]);

  const filtered = useMemo(() => {
    if (!courses) return null;
    const q = query.trim().toLowerCase();
    return courses.filter((c) => {
      if (band !== "all" && c.band !== band) return false;
      if (!q) return true;
      return (
        c.title.en.toLowerCase().includes(q) ||
        c.title.ar.includes(q) ||
        c.description.en.toLowerCase().includes(q)
      );
    });
  }, [courses, band, query]);

  async function onEnroll(course_id) {
    if (!user) {
      navigate(`/login?next=${encodeURIComponent(`/courses/${course_id}`)}`);
      return;
    }
    try {
      await api.enroll(course_id);
      setEnrolledIds((s) => new Set([...s, course_id]));
      navigate(`/courses/${course_id}`);
    } catch (e) {
      setError(e.message);
    }
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="courses-page">
      <Header />
      <section className="mx-auto max-w-7xl px-6 md:px-10 py-10 md:py-14">
        <div className="max-w-2xl">
          <h1 className="text-4xl md:text-5xl font-black tracking-tight" data-testid="courses-title">
            {t("courses.title")}
          </h1>
          <p className="mt-3 text-lg text-black/70 font-medium">{t("courses.subtitle")}</p>
        </div>

        <div className="mt-8 flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex flex-wrap gap-2" data-testid="band-tabs">
            {BANDS.map((b) => (
              <button
                key={b}
                onClick={() => setBand(b)}
                className={`rounded-full px-4 py-2 text-sm font-bold border-2 transition ${
                  band === b
                    ? "bg-[#1A1A1A] text-white border-[#1A1A1A]"
                    : "bg-white text-black/70 border-black/10 hover:border-black/30"
                }`}
                data-testid={`band-tab-${b}`}
              >
                {b === "all" ? t("courses.allBands") : t(`courses.tab${cap(b)}`)}
              </button>
            ))}
          </div>
          <div className="md:ms-auto relative w-full md:w-80">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-black/40 pointer-events-none" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("courses.searchPlaceholder")}
              className="w-full rounded-full border-2 border-black/10 bg-white ps-10 pe-4 py-3 text-sm font-medium focus:border-[#4361EE] outline-none"
              data-testid="courses-search-input"
            />
          </div>
        </div>

        {error && (
          <div
            className="mt-6 rounded-2xl bg-[#FEE2E2] text-[#991B1B] px-4 py-3 text-sm font-semibold"
            data-testid="courses-error"
          >
            {error}
          </div>
        )}

        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filtered === null ? (
            Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-72 rounded-3xl bg-white border-2 border-black/5 animate-pulse" />
            ))
          ) : filtered.length === 0 ? (
            band !== "all" ? (
              <ComingSoonBand band={band} t={t} />
            ) : (
              <div className="col-span-full text-center py-16 text-black/50 font-semibold" data-testid="courses-empty">
                {t("courses.empty")}
              </div>
            )
          ) : (
            filtered.map((c) => (
              <CourseCard
                key={c.course_id}
                course={c}
                lang={lang}
                enrolled={enrolledIds.has(c.course_id)}
                onEnroll={() => onEnroll(c.course_id)}
                t={t}
              />
            ))
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}

function cap(s) {
  return s[0].toUpperCase() + s.slice(1);
}

function ComingSoonBand({ band, t }) {
  const emoji = band === "builder" ? "🛠️" : band === "innovator" ? "🚀" : "✨";
  return (
    <div
      className="col-span-full rounded-3xl border-2 border-dashed border-black/10 bg-white p-10 md:p-14 text-center"
      data-testid={`courses-band-coming-soon-${band}`}
    >
      <div className="text-6xl md:text-7xl leading-none" aria-hidden>{emoji}</div>
      <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#FFB703]/20 text-[#7A4B00] font-black px-3 py-1 text-xs uppercase tracking-wider">
        <Sparkles className="h-3.5 w-3.5" /> {t("courses.comingSoonBadge")}
      </div>
      <h3 className="mt-4 text-2xl md:text-3xl font-black tracking-tight">
        {t(`courses.comingSoonTitle${cap(band)}`)}
      </h3>
      <p className="mt-3 text-sm md:text-base text-black/60 font-medium max-w-lg mx-auto leading-relaxed">
        {t("courses.comingSoonBody")}
      </p>
    </div>
  );
}

function CourseCard({ course, lang, enrolled, onEnroll, t }) {
  return (
    <div
      className="group relative rounded-3xl bg-white border-2 border-black/5 overflow-hidden shadow-[0_8px_30px_rgba(0,0,0,0.04)] hover:shadow-[0_24px_50px_rgba(0,0,0,0.10)] hover:-translate-y-1 transition-all duration-300"
      data-testid={`course-card-${course.course_id}`}
    >
      <div
        className="relative h-36 grid place-items-center text-6xl"
        style={{ background: course.cover_gradient || "linear-gradient(135deg,#4CC9F0,#4361EE)" }}
      >
        <span>{course.cover_emoji || "✨"}</span>
        <span className="absolute top-3 start-3 rounded-full bg-white/90 text-black/80 text-[10px] font-black uppercase tracking-wider px-2 py-1">
          {course.band}
        </span>
        <span className="absolute top-3 end-3 rounded-full bg-black/70 text-white text-[10px] font-black px-2 py-1">
          <Sparkles className="inline h-3 w-3 me-1" /> {t("courses.cardFreePreview")}
        </span>
      </div>
      <div className="p-6">
        <h3 className="text-xl font-extrabold tracking-tight text-[#1A1A1A]" data-testid={`course-title-${course.course_id}`}>
          {course.title[lang] || course.title.en}
        </h3>
        <p className="mt-2 text-sm text-black/70 leading-relaxed line-clamp-3">
          {course.description[lang] || course.description.en}
        </p>
        <div className="mt-4 flex items-center justify-between text-xs font-bold text-black/60">
          <span>{course.module_count} {t("courses.cardModules")}</span>
          <span>{t("courses.difficulty")}: {"★".repeat(course.difficulty)}{"☆".repeat(5 - course.difficulty)}</span>
        </div>
        <div className="mt-5 flex gap-2">
          <Link
            to={`/courses/${course.course_id}`}
            className="flex-1 text-center rounded-full bg-white border-2 border-black/10 hover:border-black/30 font-bold px-4 py-2.5 text-sm transition"
            data-testid={`course-open-${course.course_id}`}
          >
            {t("courses.cardOpen")}
          </Link>
          {enrolled ? (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-[#DCFCE7] text-[#166534] font-black px-4 py-2.5 text-sm"
              data-testid={`course-enrolled-${course.course_id}`}
            >
              <GraduationCap className="h-4 w-4" /> {t("courses.cardEnrolled")}
            </span>
          ) : (
            <button
              onClick={onEnroll}
              className="rounded-full bg-[#4361EE] hover:bg-[#4361EE]/90 text-white font-bold px-4 py-2.5 text-sm shadow-[0_3px_0_#2A3EB1] hover:translate-y-[1px] hover:shadow-[0_2px_0_#2A3EB1] transition"
              data-testid={`course-enroll-${course.course_id}`}
            >
              {t("courses.cardEnroll")}
            </button>
          )}
        </div>
      </div>
      <div className="absolute inset-x-0 bottom-0 h-1 opacity-70" style={{ background: course.cover_gradient }} />
      {course.has_exam && (
        <div className="absolute bottom-3 end-3 opacity-60">
          <Lock className="h-3 w-3" />
        </div>
      )}
    </div>
  );
}
